import { Show, type Component } from 'solid-js'
import { Trans } from '@mbarzda/solid-i18next'
import type { ChurnData } from '../../../features/revenue/domain/models/churn'
import { DataType } from '../../../features/revenue/domain/models/mrr'
import { useNumberFormatter } from '../../../shared/helpers/currency-formatter'
import ComparisonTable from './ComparisonTable'
import ComparisonTableRow from './ComparisonTableRow'

interface ChurnTableProps {
  data: ChurnData[]
  currentMonth?: string
  highlightMonth?: string
  formatHeader?: (dateStr: string) => string
}

const ChurnTable: Component<ChurnTableProps> = (props) => {
  const { currencyFormatter, numberFormatter } = useNumberFormatter()

  // Derive dataType from data to prevent double updates
  const isEconomics = () => props.data?.[0]?.data_type === DataType.Economics

  const formatChurnValue = (value: number): string => {
    if (isEconomics()) {
      return currencyFormatter(value, 0)
    }
    return numberFormatter(value) ?? ''
  }

  const formatChurnRate = (value: number): string => {
    return `${(value * 100).toFixed(1)}%`
  }

  return (
    <ComparisonTable
      data={props.data}
      currentMonth={props.currentMonth}
      highlightMonth={props.highlightMonth}
      maxMonths={props.data.length}
      formatHeader={props.formatHeader}
      title={isEconomics() ? <Trans key="ls_churn_mrr" /> : <Trans key="ls_generic_users" />}
      getMonth={(item) => item.date}
      noDataFallback={
        <div class="text-center text-gray-500 py-12">
          <Trans key="ls_generic_no_data" />
        </div>
      }
      rowLabelCellClass="text-left font-semibold text-gray-900"
      rowLabelCellClassStrong="text-left font-semibold text-gray-900"
      getHeaderCellClass={({ isCurrentMonth }) => {
        const base = 'px-4 py-[14px] text-left text-sm font-semibold whitespace-nowrap text-gray-900'
        if (isCurrentMonth) {
          return `${base} border-b-2 border-[#3947F2] bg-[#EDF3FF]`
        }
        return base
      }}
      getValueCellClass={({ isCurrentMonth }) => {
        const base = 'whitespace-nowrap text-left font-semibold text-gray-900'
        if (isCurrentMonth) {
          return `${base} bg-gray-50`
        }
        return base
      }}
    >
      {(ctx) => (
        <>
          <ComparisonTableRow
            ctx={ctx}
            title={<Trans key="ls_churn_total_churn" />}
            getValue={(item) => formatChurnValue(item.totalChurn)}
          />
          {/* Interval split exists for subscriber churn only — MRR has no plan interval. */}
          <Show when={!isEconomics()}>
            <ComparisonTableRow
              ctx={ctx}
              title={<Trans key="ls_churn_annual_churn" />}
              getValue={(item) => formatChurnValue(item.annualChurn)}
            />
            <ComparisonTableRow
              ctx={ctx}
              title={<Trans key="ls_churn_monthly_churn" />}
              getValue={(item) => formatChurnValue(item.monthlyChurn)}
            />
          </Show>
          <ComparisonTableRow
            ctx={ctx}
            title={<Trans key="ls_churn_churn_rate" />}
            getValue={(item) => formatChurnRate(item.churnRate)}
          />
        </>
      )}
    </ComparisonTable>
  )
}

export default ChurnTable
